import React, { useState, useEffect, useMemo } from 'react';
import * as api from '../../services/api';
import {
  X, Save, Play, AlertCircle, CheckCircle, Code, Hash, BookOpen,
  Shield, Lock, Eye, Database, Building2, Zap, Loader, Plus,
} from 'lucide-react';

/* Credo design tokens */
const BRAND = { primary: '#0d9488', dark: '#0a766b', light: '#e3f4f1', soft: '#f8d7d4' };
const S = {
  0: '#ffffff', 25: '#fdfdfc', 50: '#fafaf9', 100: '#f5f5f4', 150: '#eeeded',
  200: '#e7e5e4', 300: '#d6d3d1', 400: '#a8a29e', 500: '#78716c',
  600: '#57534e', 700: '#44403c', 800: '#292524', 900: '#1c1917',
};
const ACCENT = {
  emerald: { bg: '#ecfdf5', text: '#065f46', border: '#a7f3d0', icon: '#10b981' },
  amber:   { bg: '#fffbeb', text: '#92400e', border: '#fde68a', icon: '#f59e0b' },
  sky:     { bg: '#f0f9ff', text: '#0c4a6e', border: '#bae6fd', icon: '#0ea5e9' },
  rose:    { bg: '#fff1f2', text: '#9f1239', border: '#fecdd3', icon: '#f43f5e' },
  violet:  { bg: '#f5f3ff', text: '#5b21b6', border: '#ddd6fe', icon: '#8b5cf6' },
  slate:   { bg: '#f8fafc', text: '#475569', border: '#e2e8f0', icon: '#64748b' },
};

const CATEGORIES = [
  { v: 'PII',        l: 'PII',         Icon: Eye,       desc: 'Personally identifiable information', accent: ACCENT.violet },
  { v: 'CREDENTIAL', l: 'Credential',  Icon: Lock,      desc: 'API keys, tokens, passwords',          accent: ACCENT.rose },
  { v: 'FINANCIAL',  l: 'Financial',   Icon: Database,  desc: 'Card numbers, account data',           accent: ACCENT.amber },
  { v: 'HEALTH',     l: 'Health',      Icon: Shield,    desc: 'PHI, medical records',                 accent: ACCENT.sky },
  { v: 'CORPORATE',  l: 'Corporate',   Icon: Building2, desc: 'Trade secrets, internal data',         accent: ACCENT.emerald },
  { v: 'CUSTOM',     l: 'Custom',      Icon: Zap,       desc: 'Tenant-defined category',              accent: ACCENT.slate },
];

const DETECTION_METHODS = [
  { v: 'REGEX',      l: 'Regular expression',  desc: 'Pattern matching with optional flags' },
  { v: 'DICTIONARY', l: 'Dictionary',          desc: 'Match against a list of terms' },
  { v: 'CHECKSUM',   l: 'Checksum-validated',  desc: 'Algorithmic validation (Luhn etc.) on candidate matches' },
  { v: 'FIELD_NAME', l: 'Field name',          desc: 'Match on what a field is called, whatever its value' },
  { v: 'NER',        l: 'Named entity (Phase 4)', desc: 'ML-based — not yet implemented' },
];

const ACTIONS = [
  { v: 'BLOCK',    l: 'Block',    desc: 'Reject the entire scan; nothing flows downstream', color: '#dc2626', bg: '#fef2f2', border: '#fecaca' },
  { v: 'REDACT',   l: 'Redact',   desc: 'Replace match with [REDACTED:category]',           color: '#9f1239', bg: '#fff1f2', border: '#fecdd3' },
  { v: 'TOKENIZE', l: 'Tokenize', desc: 'Replace with vault token (reversible by policy)',   color: '#5b21b6', bg: '#f5f3ff', border: '#ddd6fe' },
  { v: 'MASK',     l: 'Mask',     desc: 'Partial mask, e.g. ****1234',                       color: '#92400e', bg: '#fffbeb', border: '#fde68a' },
  { v: 'LOG',      l: 'Log only', desc: 'Detect & audit, do not modify the value',           color: '#475569', bg: '#f8fafc', border: '#e2e8f0' },
];

const inputBase = {
  width: '100%', padding: '9px 12px', border: `1px solid ${S[200]}`, borderRadius: 8,
  fontSize: 13, fontFamily: 'inherit', background: '#fff', color: S[900],
  outline: 'none', boxSizing: 'border-box',
};
const labelStyle = { fontSize: 11, fontWeight: 700, color: S[600], textTransform: 'uppercase',
  letterSpacing: '0.06em', marginBottom: 6, display: 'block' };

const SYSTEM_TENANT_ID = '00000000-0000-0000-0000-000000000011';

/* ═══════════════════════════════════════════════════════════════════ */
export default function DataClassificationRuleEditor({ rule, onClose, onSave }) {
  const isEdit = !!rule;
  const isSystemRule = isEdit && rule.tenantId === SYSTEM_TENANT_ID;

  const [form, setForm] = useState(() => ({
    name: rule?.name || '',
    description: rule?.description || '',
    category: rule?.category || 'PII',
    detectionMethod: rule?.detectionMethod || 'REGEX',
    detectionConfig: rule?.detectionConfig || {},
    action: rule?.action || 'REDACT',
    sensitivityLevel: rule?.sensitivityLevel || 3,
    priority: rule?.priority || 100,
    enabled: rule?.enabled !== undefined ? rule.enabled : true,
    complianceFramework: rule?.complianceFramework || '',
    applicableConnectorTypes: rule?.applicableConnectorTypes || null,
    orgId: rule?.orgId || null,
  }));
  const [sample, setSample] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);

  // Update form when prop changes (e.g. switching from create→edit)
  useEffect(() => {
    if (rule) {
      setForm({
        name: rule.name || '',
        description: rule.description || '',
        category: rule.category || 'PII',
        detectionMethod: rule.detectionMethod || 'REGEX',
        detectionConfig: rule.detectionConfig || {},
        action: rule.action || 'REDACT',
        sensitivityLevel: rule.sensitivityLevel || 3,
        priority: rule.priority || 100,
        enabled: rule.enabled !== undefined ? rule.enabled : true,
        complianceFramework: rule.complianceFramework || '',
        applicableConnectorTypes: rule.applicableConnectorTypes || null,
        orgId: rule.orgId || null,
      });
    }
  }, [rule]);

  const set = (patch) => setForm(prev => ({ ...prev, ...patch }));

  const handleTest = async () => {
    if (!sample.trim()) {
      setTestResult({ error: 'Enter sample text first.' });
      return;
    }
    setTesting(true);
    try {
      const res = await api.testDpgPattern({
        detectionMethod: form.detectionMethod,
        detectionConfig: form.detectionConfig,
        sampleText: sample,
      });
      setTestResult(res.data);
    } catch (e) {
      setTestResult({ error: e.message });
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      alert('Name is required.');
      return;
    }
    setSaving(true);
    try {
      // Strip orgId/tenantId — backend will resolve these from the auth context.
      // Send only payload fields the backend allows clients to set.
      const payload = {
        name: form.name.trim(),
        description: form.description || null,
        category: form.category,
        detectionMethod: form.detectionMethod,
        detectionConfig: form.detectionConfig || {},
        action: form.action,
        sensitivityLevel: form.sensitivityLevel,
        priority: form.priority,
        enabled: form.enabled,
        complianceFramework: form.complianceFramework || null,
        applicableConnectorTypes: form.applicableConnectorTypes || null,
        orgId: form.orgId || null,
      };
      await onSave(payload);
    } finally {
      setSaving(false);
    }
  };

  const matchHighlight = useMemo(() => {
    if (!testResult || !testResult.matches || testResult.matches.length === 0) return null;
    const matches = [...testResult.matches].sort((a, b) => a.start - b.start);
    const out = [];
    let cursor = 0;
    matches.forEach((m, i) => {
      if (m.start > cursor) out.push(<span key={`t${i}`}>{sample.slice(cursor, m.start)}</span>);
      out.push(
        <mark key={`m${i}`} style={{
          background: '#fef3c7', color: '#78350f', padding: '0 2px', borderRadius: 3,
          fontWeight: 600,
        }}>{sample.slice(m.start, m.end)}</mark>
      );
      cursor = m.end;
    });
    if (cursor < sample.length) out.push(<span key="tail">{sample.slice(cursor)}</span>);
    return out;
  }, [testResult, sample]);

  return (
    <div role="dialog" aria-modal="true"
      style={{ position: 'fixed', inset: 0, background: 'rgba(28,25,23,0.55)', zIndex: 1000,
        display: 'flex', justifyContent: 'flex-end', animation: 'pcpFadeIn 0.18s ease' }}
      onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{
        width: 720, maxWidth: '100vw', height: '100vh', background: '#fff',
        boxShadow: '-12px 0 48px rgba(0,0,0,0.18)', display: 'flex', flexDirection: 'column',
        animation: 'pcpSlideLeft 0.22s ease',
      }}>
        {/* Header */}
        <div style={{ padding: '18px 24px', borderBottom: `1px solid ${S[150]}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: S[900] }}>
              {isEdit ? 'Edit classification rule' : 'New classification rule'}
            </div>
            <div style={{ fontSize: 12, color: S[500], marginTop: 2 }}>
              {isSystemRule ? 'System default — Platform Admin only' :
                isEdit ? 'Modify rule definition and enforcement action' : 'Define detection pattern + enforcement action'}
            </div>
          </div>
          <button onClick={onClose} title="Close"
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: S[500], padding: 6 }}>
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
          {/* Basics */}
          <div style={{ marginBottom: 20 }}>
            <SectionHeader>Identity</SectionHeader>
            <Row>
              <Field label="Name" required>
                <input value={form.name} onChange={(e) => set({ name: e.target.value })}
                  placeholder="e.g. US Social Security Number"
                  disabled={isSystemRule}
                  style={inputBase} />
              </Field>
              <Field label="Compliance framework" hint="e.g. HIPAA, PCI-DSS, GDPR">
                <input value={form.complianceFramework}
                  onChange={(e) => set({ complianceFramework: e.target.value })}
                  placeholder="HIPAA, PCI-DSS, SOC2"
                  disabled={isSystemRule}
                  style={inputBase} />
              </Field>
            </Row>
            <Field label="Description">
              <textarea rows={2} value={form.description}
                onChange={(e) => set({ description: e.target.value })}
                placeholder="What this rule detects and why it matters"
                disabled={isSystemRule}
                style={{ ...inputBase, resize: 'vertical', fontFamily: 'inherit' }} />
            </Field>
          </div>

          {/* Category */}
          <div style={{ marginBottom: 20 }}>
            <SectionHeader>Category</SectionHeader>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
              {CATEGORIES.map(c => {
                const selected = form.category === c.v;
                return (
                  <button key={c.v} type="button" onClick={() => set({ category: c.v })}
                    disabled={isSystemRule}
                    style={{
                      textAlign: 'left', padding: '10px 12px', borderRadius: 10, cursor: isSystemRule ? 'not-allowed' : 'pointer',
                      border: selected ? `2px solid ${c.accent.icon}` : `1px solid ${S[200]}`,
                      background: selected ? c.accent.bg : '#fff',
                      transition: 'all 0.12s ease', display: 'flex', alignItems: 'flex-start', gap: 10,
                    }}>
                    <div style={{ width: 28, height: 28, borderRadius: 8, background: c.accent.bg,
                      border: `1px solid ${c.accent.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0 }}>
                      <c.Icon size={14} color={c.accent.icon} />
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: S[900] }}>{c.l}</div>
                      <div style={{ fontSize: 10, color: S[500], marginTop: 2, lineHeight: 1.4 }}>{c.desc}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Detection */}
          <div style={{ marginBottom: 20 }}>
            <SectionHeader>Detection method</SectionHeader>
            <select value={form.detectionMethod} onChange={(e) => set({ detectionMethod: e.target.value, detectionConfig: {} })}
              disabled={isSystemRule}
              style={{ ...inputBase, marginBottom: 12 }}>
              {DETECTION_METHODS.map(m => <option key={m.v} value={m.v}>{m.l} — {m.desc}</option>)}
            </select>

            <DetectionConfigEditor
              method={form.detectionMethod}
              config={form.detectionConfig}
              onChange={(c) => set({ detectionConfig: c })}
              disabled={isSystemRule}
            />
          </div>

          {/* Test pattern */}
          <div style={{ marginBottom: 20, padding: '14px 16px', background: S[25], borderRadius: 10, border: `1px solid ${S[200]}` }}>
            <SectionHeader>
              {form.detectionMethod === 'FIELD_NAME' ? 'Test a field name' : 'Test against sample text'}
            </SectionHeader>
            <textarea rows={3} value={sample} onChange={(e) => setSample(e.target.value)}
              placeholder={form.detectionMethod === 'FIELD_NAME'
                ? "Enter one field name (e.g. 'ssn') to check whether this rule covers it"
                : "Paste a sample input (e.g. 'My SSN is 123-45-6789') to verify the pattern matches what you expect"}
              style={{ ...inputBase, fontFamily: 'ui-monospace, SFMono-Regular, monospace', fontSize: 12 }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
              <button onClick={handleTest} disabled={testing}
                style={{ background: S[800], color: '#fff', border: 'none', cursor: 'pointer', borderRadius: 8,
                  padding: '8px 14px', fontSize: 13, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                {testing ? <Loader size={14} style={{ animation: 'pcpSpin 0.8s linear infinite' }} /> : <Play size={14} />}
                {testing ? 'Testing…' : 'Run test'}
              </button>
              {testResult && !testResult.error && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6,
                  color: testResult.matched ? ACCENT.emerald.text : S[600], fontSize: 12, fontWeight: 600 }}>
                  {testResult.matched
                    ? <><CheckCircle size={14} color={ACCENT.emerald.icon} /> {testResult.matches.length} match{testResult.matches.length !== 1 ? 'es' : ''}</>
                    : <>No matches</>}
                </div>
              )}
              {testResult?.error && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: ACCENT.rose.text, fontSize: 12 }}>
                  <AlertCircle size={14} color={ACCENT.rose.icon} /> {testResult.error}
                </div>
              )}
            </div>
            {testResult?.note && (
              <div style={{ marginTop: 8, padding: '6px 10px', background: ACCENT.amber.bg, border: `1px solid ${ACCENT.amber.border}`,
                color: ACCENT.amber.text, fontSize: 11, borderRadius: 6 }}>
                {testResult.note}
              </div>
            )}
            {matchHighlight && (
              <div style={{ marginTop: 10, padding: '10px 12px', background: '#fff', border: `1px solid ${S[200]}`,
                borderRadius: 8, fontSize: 13, fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: S[800] }}>
                {matchHighlight}
              </div>
            )}
          </div>

          {/* Action */}
          <div style={{ marginBottom: 20 }}>
            <SectionHeader>Enforcement action</SectionHeader>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 6, marginBottom: 8 }}>
              {ACTIONS.map(a => {
                const selected = form.action === a.v;
                return (
                  <button key={a.v} type="button" onClick={() => set({ action: a.v })}
                    disabled={isSystemRule}
                    title={a.desc}
                    style={{
                      padding: '8px 10px', borderRadius: 8, cursor: isSystemRule ? 'not-allowed' : 'pointer',
                      border: selected ? `2px solid ${a.color}` : `1px solid ${S[200]}`,
                      background: selected ? a.bg : '#fff',
                      fontSize: 12, fontWeight: 700, color: selected ? a.color : S[700],
                      transition: 'all 0.12s ease', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.04em',
                    }}>
                    {a.l}
                  </button>
                );
              })}
            </div>
            <div style={{ fontSize: 11, color: S[500], lineHeight: 1.5 }}>
              {ACTIONS.find(a => a.v === form.action)?.desc}
            </div>
          </div>

          {/* Sensitivity & priority */}
          <Row>
            <Field label="Sensitivity level" hint="1 = informational • 5 = critical">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input type="range" min={1} max={5} step={1} value={form.sensitivityLevel}
                  onChange={(e) => set({ sensitivityLevel: parseInt(e.target.value, 10) })}
                  disabled={isSystemRule}
                  style={{ flex: 1 }} />
                <div style={{ minWidth: 52, textAlign: 'right' }}>
                  <SensitivityBadge level={form.sensitivityLevel} />
                </div>
              </div>
            </Field>
            <Field label="Priority" hint="lower = checked first; ties break by sensitivity">
              <input type="number" min={1} max={9999} value={form.priority}
                onChange={(e) => set({ priority: Math.max(1, parseInt(e.target.value, 10) || 100) })}
                disabled={isSystemRule}
                style={inputBase} />
            </Field>
          </Row>

          {/* Status & scope */}
          <Row>
            <Field label="Status">
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, color: S[800], cursor: 'pointer', padding: '9px 0' }}>
                <input type="checkbox" checked={form.enabled} onChange={(e) => set({ enabled: e.target.checked })} />
                Enabled — runtime evaluates this rule on every scan
              </label>
            </Field>
            <Field label="Org scope" hint="Leave empty for tenant-wide; set to override for one org">
              <input value={form.orgId || ''}
                onChange={(e) => set({ orgId: e.target.value.trim() || null })}
                placeholder="(tenant-wide)"
                disabled={isSystemRule}
                style={{ ...inputBase, fontFamily: 'ui-monospace, SFMono-Regular, monospace', fontSize: 12 }} />
            </Field>
          </Row>

          {/* Connector-type scope */}
          <div style={{ marginBottom: 20 }}>
            <SectionHeader>Connector-type scope</SectionHeader>
            <ConnectorTypePicker
              value={form.applicableConnectorTypes}
              onChange={(v) => set({ applicableConnectorTypes: v })}
              disabled={isSystemRule}
            />
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 24px', borderTop: `1px solid ${S[150]}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexShrink: 0, background: S[25] }}>
          <div style={{ fontSize: 11, color: S[500] }}>
            {isSystemRule
              ? 'System defaults are read-only unless you are a Platform Admin'
              : 'Saving will invalidate the DPG cache so the next scan picks up your change'}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose}
              style={{ background: '#fff', color: S[700], border: `1px solid ${S[200]}`, cursor: 'pointer',
                borderRadius: 8, padding: '9px 16px', fontSize: 13 }}>Cancel</button>
            <button onClick={handleSave} disabled={saving || isSystemRule}
              style={{ background: BRAND.primary, color: '#fff', border: 'none',
                cursor: (saving || isSystemRule) ? 'not-allowed' : 'pointer', opacity: isSystemRule ? 0.5 : 1,
                borderRadius: 8, padding: '9px 18px', fontSize: 13, fontWeight: 600,
                display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {saving ? <Loader size={14} style={{ animation: 'pcpSpin 0.8s linear infinite' }} /> : <Save size={14} />}
              {isEdit ? 'Save changes' : 'Create rule'}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes pcpSlideLeft { from { transform: translateX(40px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        @keyframes pcpSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes pcpFadeIn { from { opacity: 0; } to { opacity: 1; } }
      `}</style>
    </div>
  );
}

/* ─── DetectionConfigEditor ─────────────────────────────────────── */
function DetectionConfigEditor({ method, config, onChange, disabled }) {
  if (method === 'REGEX') {
    return (
      <>
        <Field label="Pattern (RE2 / Java regex)" required hint="Tip: use \\b for word boundaries to avoid false positives">
          <input value={config.pattern || ''}
            onChange={(e) => onChange({ ...config, pattern: e.target.value })}
            placeholder="\\b\\d{3}-\\d{2}-\\d{4}\\b"
            disabled={disabled}
            style={{ ...inputBase, fontFamily: 'ui-monospace, SFMono-Regular, monospace', fontSize: 12 }} />
        </Field>
        <Field label="Flags" hint="CASE_INSENSITIVE, MULTILINE — comma-separated">
          <input value={config.flags || ''}
            onChange={(e) => onChange({ ...config, flags: e.target.value })}
            placeholder="CASE_INSENSITIVE"
            disabled={disabled}
            style={inputBase} />
        </Field>
        <Field label="Context patterns (optional)" hint="Words near the match that boost confidence — e.g. 'ssn', 'social security'">
          <input value={(config.contextPatterns || []).join(', ')}
            onChange={(e) => onChange({ ...config, contextPatterns: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
            placeholder="ssn, social security, social_security"
            disabled={disabled}
            style={inputBase} />
        </Field>
      </>
    );
  }

  if (method === 'DICTIONARY') {
    return (
      <>
        <Field label="Terms" required hint="Comma-separated list — case-insensitive">
          <textarea rows={3} value={(config.terms || []).join(', ')}
            onChange={(e) => onChange({ ...config, terms: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
            placeholder="confidential, internal use only, do not distribute"
            disabled={disabled}
            style={{ ...inputBase, fontFamily: 'inherit', resize: 'vertical' }} />
        </Field>
        <Field label="Match mode">
          <select value={config.matchMode || 'WORD_BOUNDARY'}
            onChange={(e) => onChange({ ...config, matchMode: e.target.value })}
            disabled={disabled}
            style={inputBase}>
            <option value="WORD_BOUNDARY">Whole word — recommended</option>
            <option value="SUBSTRING">Substring — matches anywhere</option>
          </select>
        </Field>
      </>
    );
  }

  if (method === 'CHECKSUM') {
    return (
      <>
        <Field label="Algorithm" required>
          <select value={config.algorithm || 'LUHN'}
            onChange={(e) => onChange({ ...config, algorithm: e.target.value })}
            disabled={disabled}
            style={inputBase}>
            <option value="LUHN">LUHN — credit cards, IMEI</option>
          </select>
        </Field>
        <Field label="Candidate pattern" required hint="Regex that finds candidates; checksum gates the final decision">
          <input value={config.pattern || ''}
            onChange={(e) => onChange({ ...config, pattern: e.target.value })}
            placeholder="\\b(?:\\d[ -]*?){13,19}\\b"
            disabled={disabled}
            style={{ ...inputBase, fontFamily: 'ui-monospace, SFMono-Regular, monospace', fontSize: 12 }} />
        </Field>
        <Field label="Allowed length range" hint="e.g. [13, 19] for major card networks">
          <Row>
            <input type="number" min={1} max={64} placeholder="13"
              value={config.lengthRange?.[0] || ''}
              onChange={(e) => onChange({ ...config, lengthRange: [parseInt(e.target.value, 10), config.lengthRange?.[1] || 19] })}
              disabled={disabled}
              style={inputBase} />
            <input type="number" min={1} max={64} placeholder="19"
              value={config.lengthRange?.[1] || ''}
              onChange={(e) => onChange({ ...config, lengthRange: [config.lengthRange?.[0] || 13, parseInt(e.target.value, 10)] })}
              disabled={disabled}
              style={inputBase} />
          </Row>
        </Field>
      </>
    );
  }

  if (method === 'FIELD_NAME') {
    return (
      <>
        <Field label="Field names" required
          hint="Comma-separated. Matched against the field's name, not its contents, and not case-sensitive.">
          <textarea rows={3} value={(config.fieldNames || []).join(', ')}
            onChange={(e) => onChange({ ...config, fieldNames: e.target.value.split(',').map(v => v.trim()).filter(Boolean) })}
            placeholder="ssn, social_security, socialSecurity, tax_id"
            disabled={disabled}
            style={{ ...inputBase, fontFamily: 'ui-monospace, SFMono-Regular, monospace', fontSize: 12, resize: 'vertical' }} />
        </Field>
        <div style={{ padding: '10px 12px', background: ACCENT.amber.bg, border: `1px solid ${ACCENT.amber.border}`,
          borderRadius: 8, color: ACCENT.amber.text, fontSize: 12, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <AlertCircle size={15} color={ACCENT.amber.icon} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>
            A match here covers the field's <b>entire value</b>, because what makes it sensitive is
            what the field is rather than something found inside it. Name a field that holds
            ordinary data and every value in it will be acted on.
          </span>
        </div>
      </>
    );
  }

  if (method === 'NER') {
    return (
      <div style={{ padding: '14px 16px', background: ACCENT.amber.bg, border: `1px solid ${ACCENT.amber.border}`,
        borderRadius: 10, color: ACCENT.amber.text, fontSize: 13, display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <AlertCircle size={16} color={ACCENT.amber.icon} style={{ flexShrink: 0, marginTop: 1 }} />
        <div>
          <div style={{ fontWeight: 700, marginBottom: 2 }}>Named-entity detection is not yet available</div>
          <div>Choose REGEX or DICTIONARY to author this rule.</div>
        </div>
      </div>
    );
  }
  return null;
}

/* ─── Layout helpers ─────────────────────────────────────────── */
function SectionHeader({ children }) {
  return (
    <div style={{ fontSize: 12, fontWeight: 700, color: S[900], marginBottom: 10,
      paddingBottom: 6, borderBottom: `1px solid ${S[150]}` }}>{children}</div>
  );
}
function Row({ children }) {
  return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>{children}</div>;
}
function Field({ label, hint, required, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={labelStyle}>
        {label}
        {required && <span style={{ color: BRAND.primary, marginLeft: 4 }}>*</span>}
      </label>
      {children}
      {hint && <div style={{ fontSize: 10, color: S[500], marginTop: 4 }}>{hint}</div>}
    </div>
  );
}
/**
 * ConnectorTypePicker — chip-style multi-select.
 *
 * Empty / null value === "applies to all connectors" (the runtime semantic in
 * `DataClassificationRule.appliesToConnector` returns true on null/empty array).
 * A populated array restricts the rule so it only fires when the active
 * connector type matches one in the list.
 *
 * Why this is two stages:
 *  - Suggested types are the canonical lowercase strings the runtime sees
 *    (matches `connectorType` on `DataProtectionGate.ScanContext`). Click to
 *    toggle.
 *  - Custom types: free-text add for tenants with bespoke connectors. Stored
 *    as-typed (no normalization) so it has to match exactly what the runtime
 *    receives — the hint text below the input warns about this.
 */
const SUGGESTED_CONNECTOR_TYPES = [
  'salesforce', 'servicenow', 'jira', 'slack', 'github',
  'snowflake', 'workday', 'okta', 'pagerduty', 'http',
  'kafka', 'database', 'email',
];

function ConnectorTypePicker({ value, onChange, disabled }) {
  const [custom, setCustom] = React.useState('');
  // Normalize: array of strings; null/undefined treated as []
  const selected = Array.isArray(value) ? value.map(v => String(v)) : [];
  const isApplyAll = selected.length === 0;

  const toggle = (t) => {
    if (disabled) return;
    if (selected.includes(t)) onChange(selected.filter(x => x !== t));
    else onChange([...selected, t]);
  };
  const addCustom = () => {
    const v = custom.trim();
    if (!v || disabled) return;
    if (!selected.includes(v)) onChange([...selected, v]);
    setCustom('');
  };
  const clearAll = () => onChange(null);

  return (
    <div>
      <div style={{
        padding: '10px 12px', borderRadius: 8,
        background: isApplyAll ? ACCENT.emerald.bg : ACCENT.sky.bg,
        border: `1px solid ${isApplyAll ? ACCENT.emerald.border : ACCENT.sky.border}`,
        color: isApplyAll ? ACCENT.emerald.text : ACCENT.sky.text,
        fontSize: 12, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8,
      }}>
        {isApplyAll
          ? <><CheckCircle size={14} color={ACCENT.emerald.icon} /> Applies to <strong>all connector types</strong> (default).</>
          : <><AlertCircle size={14} color={ACCENT.sky.icon} /> Restricted to <strong>{selected.length}</strong> connector type{selected.length !== 1 ? 's' : ''} — won't fire on others.</>}
      </div>

      {/* Selected chips */}
      {selected.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
          {selected.map(t => (
            <span key={t} style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '4px 10px', borderRadius: 999,
              background: BRAND.light, border: `1px solid ${BRAND.soft}`,
              color: BRAND.dark, fontSize: 12, fontWeight: 600,
              fontFamily: 'ui-monospace, SFMono-Regular, monospace',
            }}>
              {t}
              {!disabled && (
                <button onClick={() => toggle(t)} title="Remove"
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: BRAND.dark,
                    padding: 0, display: 'inline-flex', alignItems: 'center' }}>
                  <X size={12} />
                </button>
              )}
            </span>
          ))}
          {!disabled && (
            <button onClick={clearAll}
              style={{ background: 'transparent', border: `1px dashed ${S[300]}`, color: S[600],
                cursor: 'pointer', borderRadius: 999, padding: '3px 10px', fontSize: 11 }}>
              Clear (apply to all)
            </button>
          )}
        </div>
      )}

      {/* Suggested chips */}
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: S[600], textTransform: 'uppercase',
          letterSpacing: '0.06em', marginBottom: 6 }}>Suggested</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {SUGGESTED_CONNECTOR_TYPES.map(t => {
            const isSelected = selected.includes(t);
            return (
              <button key={t} onClick={() => toggle(t)}
                disabled={disabled}
                style={{
                  padding: '4px 10px', borderRadius: 999, fontSize: 11,
                  fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  border: isSelected ? `1px solid ${BRAND.primary}` : `1px solid ${S[200]}`,
                  background: isSelected ? BRAND.light : '#fff',
                  color: isSelected ? BRAND.dark : S[700], fontWeight: 600,
                  transition: 'all 0.12s ease',
                }}>
                {isSelected ? '✓ ' : '+ '}{t}
              </button>
            );
          })}
        </div>
      </div>

      {/* Custom add */}
      <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
        <input value={custom}
          onChange={(e) => setCustom(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustom(); } }}
          placeholder="Add a custom connector type (lowercase, e.g. 'mongodb')"
          disabled={disabled}
          style={{ ...inputBase, flex: 1, fontFamily: 'ui-monospace, SFMono-Regular, monospace', fontSize: 12 }} />
        <button onClick={addCustom} disabled={disabled || !custom.trim()}
          style={{ background: S[800], color: '#fff', border: 'none',
            cursor: (disabled || !custom.trim()) ? 'not-allowed' : 'pointer', opacity: (disabled || !custom.trim()) ? 0.5 : 1,
            borderRadius: 8, padding: '9px 16px', fontSize: 13, fontWeight: 600,
            display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Plus size={14} /> Add
        </button>
      </div>
      <div style={{ fontSize: 10, color: S[500], marginTop: 6, lineHeight: 1.4 }}>
        Use the exact lowercase identifier the runtime sees in <code style={{ background: S[100], padding: '0 4px', borderRadius: 3 }}>ScanContext.connectorType</code>.
        Mismatched values cause the rule to silently never fire — when in doubt, leave this empty so the rule applies to all scans.
      </div>
    </div>
  );
}

function SensitivityBadge({ level }) {
  const labels = ['—', 'Low', 'Mod', 'Med', 'High', 'Crit'];
  const colors = ['#84cc16', '#a3e635', '#facc15', '#fb923c', '#dc2626'];
  const lvl = Math.min(5, Math.max(1, level || 1));
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{ fontSize: 16, fontWeight: 800, color: colors[lvl - 1], fontFeatureSettings: "'tnum'" }}>{lvl}</span>
      <span style={{ fontSize: 11, fontWeight: 700, color: colors[lvl - 1], textTransform: 'uppercase', letterSpacing: '0.05em' }}>{labels[lvl]}</span>
    </div>
  );
}
